/**
 * useDeferredUndo — one-shot undo tracking for live-preview interactions.
 *
 * Live operators and slider-driven panels re-apply on every interaction
 * tick from a captured baseline. We want exactly one undo entry to cover
 * the whole session regardless of how many ticks happen. Multiple surfaces
 * used to reinvent the same `undoPushedRef = useRef(false)` pattern — this
 * hook is the shared implementation.
 *
 * Usage:
 * ```
 * const undo = useDeferredUndo();
 * // In open effect: undo.reset()
 * // In first mutation: undo.pushOnce()
 * // In close handler: undo.reset()
 * ```
 *
 * `pushOnce` calls `useStore.getState().pushUndo()` exactly once per session.
 * `reset` clears the flag so the next session pushes a fresh undo entry.
 */

import { useMemo, useRef } from "react";
import { useStore } from "../../app/store.ts";

export interface DeferredUndo {
  /** Push an undo entry exactly once per session. Subsequent calls no-op. */
  pushOnce: () => void;
  /** Clear the session so the next `pushOnce` call pushes a fresh entry. */
  reset: () => void;
}

// Returned object identity is stable across renders so callers can put `undo`
// directly into useEffect/useCallback dep arrays without retriggering. A fresh
// object literal here caused an infinite render loop in GravityMenu — the
// open-effect's deps included a callback whose deps included `undo`, so each
// render minted a new function which retriggered the effect.
export function useDeferredUndo(): DeferredUndo {
  const pushedRef = useRef(false);

  return useMemo<DeferredUndo>(
    () => ({
      pushOnce: () => {
        if (pushedRef.current) return;
        useStore.getState().pushUndo();
        pushedRef.current = true;
      },
      reset: () => {
        pushedRef.current = false;
      },
    }),
    [],
  );
}
